import lodash from 'lodash';
import Helpers from '../base/Helpers';
import Response from "../base/Response";
import ServiceBehaviour from '../base/ServiceBehaviour';
import InvalidTransactionTypeException from '../exceptions/InvalidTransactionTypeException';
import RunningTransactionException from '../exceptions/RunningTransactionException';
import SaleTransaction from '../requests/SaleTransaction';
import AuthTransaction from '../requests/AuthTransaction';
import CaptureTransaction from '../requests/CaptureTransaction';
import VoidTransaction from '../requests/VoidTransaction';
import StatusTransaction from '../requests/StatusTransaction';
import Settings from '../models/Settings';
import Loading from '../libraries/PixelPayLoading';
import PayloadResponse from '../responses/PayloadResponse';
import CardinalManager from '../libraries/CardinalManager';
import PayloadManager from '../libraries/PayloadManager';
import CybersourceManager from '../libraries/CybersourceManager';
import RouterManager from '../libraries/RouterManager';

export default class Transaction extends ServiceBehaviour {
	/**
	 * Define if there's a transaction currently running
	 */
	static busy: boolean = false;

	/**
	 * Define if concurrent transactions should be allowed
	 */
	static concurrent: boolean = false;

	/**
	 * Initialize service
	 */
	constructor(settings: Settings) {
		super(settings);
	}

	/**
	 * Enable concurrent transactions
	 */
	static withConcurrency() {
		Transaction.concurrent = true;
	}

	/**
	 * Send and processing SALE transaction
	 *
	 * @param transaction
	 * @return
	 * @throws InvalidCredentialsException
	 */
	async doSale(transaction: SaleTransaction): Promise<Response> {
		await transaction.doBeforeExecute();

		if (transaction.authentication_request && !Helpers.checkIsBrowser()) {
			throw new InvalidTransactionTypeException("This platform not support 3DS transactions");
		}

		if (!Transaction.concurrent && Transaction.busy) {
			throw new RunningTransactionException("There's an active transaction still running.");
		}

		try {
			Loading.show();
			Transaction.busy = true;
			const router_transaction = lodash.cloneDeep(transaction);

			const response = await this.post("api/v2/transaction/sale", transaction);

			if (response instanceof PayloadResponse) {
				switch (response.data?.transaction_type) {
					case 'songbird':
						return await (new CardinalManager(this.settings, transaction)).init(
							response.data.payload,
							response.data?.collection_url
						);

					case 'payload':
						return await (new PayloadManager(this.settings)).loadPayload(response.data.payload);

					case 'router':
						return await (new RouterManager(router_transaction)).init(response.data.payload);

					default:
						throw new RunningTransactionException("This type of transaction is invalid");
				}
			}

			CardinalManager.clearAllEvents();
			return response;
		} catch (err) {
			CardinalManager.clearAllEvents();
			throw err;
		} finally {
			Loading.hide();
			Transaction.busy = false;
		}
	}

	/**
	 * Send and processing AUTH transaction
	 *
	 * @param transaction
	 * @return
	 * @throws InvalidCredentialsException
	 */
	async doAuth(transaction: AuthTransaction): Promise<Response> {
		await transaction.doBeforeExecute();

		if (transaction.authentication_request && !Helpers.checkIsBrowser()) {
			throw new InvalidTransactionTypeException("This platform not support 3DS transactions");
		}

		if (!Transaction.concurrent && Transaction.busy) {
			throw new RunningTransactionException("There's an active transaction still running.");
		}

		try {
			Loading.show();
			Transaction.busy = true;
			const router_transaction = lodash.cloneDeep(transaction);

			const response = await this.post("api/v2/transaction/auth", transaction);

			if (response instanceof PayloadResponse) {
				switch (response.data?.transaction_type) {
					case 'songbird':
						return await (new CardinalManager(this.settings, transaction)).init(
							response.data.payload,
							response.data?.collection_url,
						);

					case 'payload':
						return await (new PayloadManager(this.settings)).loadPayload(response.data.payload);

					case 'router':
						return await (new RouterManager(router_transaction)).init(response.data.payload);

					default:
						throw new RunningTransactionException("This type of transaction is invalid");
				}
			}

			CardinalManager.clearAllEvents();
			return response;
		} catch (err) {
			CardinalManager.clearAllEvents();
			throw err;
		} finally {
			Loading.hide();
			Transaction.busy = false;
		}
	}

	/**
	 * Send and processing CAPTURE transaction
	 *
	 * @param transaction
	 * @return
	 * @throws InvalidCredentialsException
	 */
	async doCapture(transaction: CaptureTransaction): Promise<Response> {
		Loading.show();
		const response = await this.post("api/v2/transaction/capture", transaction);
		Loading.hide();

		return response;
	}

	/**
	 * Send and processing VOID transaction
	 *
	 * @param transaction
	 * @return
	 * @throws InvalidCredentialsException
	 */
	async doVoid(transaction: VoidTransaction): Promise<Response> {
		Loading.show();
		const response = await this.post("api/v2/transaction/void", transaction);
		Loading.hide();

		return response;
	}

	/**
	 * Send and processing SALE transaction
	 *
	 * @param transaction
	 * @return
	 * @throws InvalidCredentialsException
	 */
	getStatus(transaction: StatusTransaction): Promise<Response> {
		return this.post("api/v2/transaction/status", transaction);
	}

	/**
	 * Verify a payment hash and returns true if payment response is not modified
	 *
	 * @param hash
	 * @param order_id
	 * @param secret
	 * @return
	 */
	verifyPaymentHash(hash: string, order_id: string, secret: string): boolean {
		if (Helpers.checkIsBrowser()) {
			throw new InvalidTransactionTypeException("This platform not support hash validations in browser/frontend");
		}

		const chain: string = [order_id, this.settings.auth_key, secret].join("|");

		return hash === Helpers.hash("MD5", chain);
	}

	/**
	 * Get Cybersource Decision Manager Fingerprint
	 *
	 * @param merchant_id
	 * @param org_id
	 * @param callback
	 */
	getCybersourceFingerprint(merchant_id: string, org_id: string): string {
		return CybersourceManager.getFingerprint(merchant_id, org_id);
	}
}
